import { useState, useEffect, useCallback } from 'react';
import { ref, onValue, set, update } from 'firebase/database';
import { db } from '../services/firebase';
import { UserProfile, getLevelForXP, MatchRecord, OwnedSkins } from '../lib/progression';
import { XP_REWARDS } from '../lib/constants';
import { trackEvent } from '../lib/analytics';

export function useUserProfile(uid: string | undefined) {
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  const getLocalData = useCallback(() => {
    try {
      const statsSaved = localStorage.getItem('sbarazzo_stats');
      const xpSaved = localStorage.getItem('sbarazzo_xp');
      const nameSaved = localStorage.getItem('sbarazzo_player_name');
      const avatarSaved = localStorage.getItem('sbarazzo_avatar');
      const titleSaved = localStorage.getItem('sbarazzo_title');
      const historySaved = localStorage.getItem('sbarazzo_match_history');

      const stats = statsSaved ? JSON.parse(statsSaved) : { played: 0, won: 0, lost: 0 };
      const xp = xpSaved ? parseInt(xpSaved, 10) : 0;
      const displayName = nameSaved || `Ospite ${Math.floor(1000 + Math.random() * 9000)}`;
      const avatar = avatarSaved || '🪙';
      const title = titleSaved || 'Novellino';
      const history = historySaved ? JSON.parse(historySaved) : [];

      return { stats, xp, displayName, avatar, title, history };
    } catch {
      return {
        stats: { played: 0, won: 0, lost: 0 },
        xp: 0,
        displayName: `Ospite ${Math.floor(1000 + Math.random() * 9000)}`,
        avatar: '🪙',
        title: 'Novellino',
        history: [] as MatchRecord[],
      };
    }
  }, []);

  const saveLocalData = useCallback((
    displayName: string,
    xp: number,
    stats: { played: number; won: number; lost: number },
    avatar: string,
    title: string,
    history: MatchRecord[],
  ) => {
    try {
      localStorage.setItem('sbarazzo_player_name', displayName);
      localStorage.setItem('sbarazzo_xp', xp.toString());
      localStorage.setItem('sbarazzo_stats', JSON.stringify(stats));
      localStorage.setItem('sbarazzo_avatar', avatar);
      localStorage.setItem('sbarazzo_title', title);
      localStorage.setItem('sbarazzo_match_history', JSON.stringify(history));
    } catch (error) {
      console.error('Failed to save local profile data:', error);
    }
  }, []);

  useEffect(() => {
    if (!uid) {
      const local = getLocalData();
      setProfile({
        uid: 'guest',
        displayName: local.displayName,
        xp: local.xp,
        level: getLevelForXP(local.xp),
        stats: local.stats,
        avatar: local.avatar,
        title: local.title,
        history: local.history,
        isPremium: false,
      });
      setLoading(false);
      return;
    }

    setLoading(true);
    const userRef = ref(db, `users/${uid}`);

    const unsubscribe = onValue(userRef, async (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const updatedProfile: UserProfile = {
          uid,
          displayName: data.displayName || 'Giocatore',
          xp: data.xp ?? 0,
          level: data.level ?? 1,
          stats: data.stats || { played: 0, won: 0, lost: 0 },
          avatar: data.avatar || '🪙',
          title: data.title || 'Novellino',
          history: data.history || [],
          isPremium: data.isPremium ?? false,
          createdAt: data.createdAt,
        };
        setProfile(updatedProfile);
        saveLocalData(
          updatedProfile.displayName,
          updatedProfile.xp,
          updatedProfile.stats,
          updatedProfile.avatar || '🪙',
          updatedProfile.title || 'Novellino',
          updatedProfile.history || [],
        );
        setLoading(false);
      } else {
        const local = getLocalData();
        const newProfile: Omit<UserProfile, 'uid'> & { createdAt: number } = {
          displayName: local.displayName,
          xp: local.xp,
          level: getLevelForXP(local.xp),
          stats: local.stats,
          avatar: local.avatar,
          title: local.title,
          history: local.history,
          isPremium: false,
          createdAt: Date.now(),
        };

        try {
          await set(userRef, newProfile);
        } catch (error) {
          console.error('[useUserProfile] Error initializing profile in Firebase:', error);
          setProfile({ uid, ...newProfile });
          setLoading(false);
        }
      }
    }, (error) => {
      console.error('[useUserProfile] listener error:', error);
      const local = getLocalData();
      setProfile({
        uid,
        displayName: local.displayName,
        xp: local.xp,
        level: getLevelForXP(local.xp),
        stats: local.stats,
        avatar: local.avatar,
        title: local.title,
        history: local.history,
        isPremium: false,
      });
      setLoading(false);
    });

    return () => unsubscribe();
  }, [uid, getLocalData, saveLocalData]);

  const updateDisplayName = useCallback(async (newName: string) => {
    const trimmed = newName.trim().slice(0, 20);
    if (!trimmed) return;

    if (profile) {
      const updated = { ...profile, displayName: trimmed };
      setProfile(updated);
      saveLocalData(trimmed, profile.xp, profile.stats, profile.avatar || '🪙', profile.title || 'Novellino', profile.history || []);
    }

    if (uid && uid !== 'guest') {
      try {
        await update(ref(db, `users/${uid}`), { displayName: trimmed });
      } catch (error) {
        console.error('[useUserProfile] Error updating name in Firebase:', error);
      }
    }
  }, [uid, profile, saveLocalData]);

  const updateProfileCustomization = useCallback(async (avatar: string, title: string) => {
    if (!profile) return;

    const updated = { ...profile, avatar, title };
    setProfile(updated);
    saveLocalData(profile.displayName, profile.xp, profile.stats, avatar, title, profile.history || []);

    if (uid && uid !== 'guest') {
      try {
        await update(ref(db, `users/${uid}`), { avatar, title });
      } catch (error) {
        console.error('[useUserProfile] Error updating customization in Firebase:', error);
      }
    }
  }, [uid, profile, saveLocalData]);

  const recordMatchOutcome = useCallback(async (
    isWin: boolean,
    mode: 'cpu' | 'online' = 'cpu',
    opponentName: string = 'Avversario',
    myScore: number = 0,
    opponentScore: number = 0,
  ): Promise<{ leveledUp: boolean; oldLevel: number; newLevel: number }> => {
    if (!profile) return { leveledUp: false, oldLevel: 0, newLevel: 0 };

    const oldLevel = profile.level;
    const xpToAdd = isWin ? XP_REWARDS.WIN_MATCH : XP_REWARDS.LOSS_MATCH;
    const newXp = profile.xp + xpToAdd;
    const newLevel = getLevelForXP(newXp);
    const newStats = {
      played: profile.stats.played + 1,
      won: profile.stats.won + (isWin ? 1 : 0),
      lost: profile.stats.lost + (isWin ? 0 : 1),
    };

    const record: MatchRecord = {
      id: Math.random().toString(36).substring(2, 9),
      timestamp: Date.now(),
      mode,
      opponentName,
      myScore,
      opponentScore,
      won: isWin,
    };

    const oldHistory = profile.history || [];
    const newHistory = [record, ...oldHistory].slice(0, 10);

    const updated: UserProfile = {
      ...profile,
      xp: newXp,
      level: newLevel,
      stats: newStats,
      history: newHistory,
    };
    setProfile(updated);
    saveLocalData(
      profile.displayName,
      newXp,
      newStats,
      profile.avatar || '🪙',
      profile.title || 'Novellino',
      newHistory,
    );

    if (uid && uid !== 'guest') {
      try {
        await update(ref(db, `users/${uid}`), {
          xp: newXp,
          level: newLevel,
          stats: newStats,
          history: newHistory,
        });
      } catch (error) {
        console.error('[useUserProfile] Error updating match outcome in Firebase:', error);
      }
    }

    trackEvent('game_complete', {
      mode,
      result: isWin ? 'win' : 'loss',
      player_score: myScore,
      opponent_score: opponentScore,
    });

    return {
      leveledUp: newLevel > oldLevel,
      oldLevel,
      newLevel,
    };
  }, [uid, profile, saveLocalData]);

  const equipSkin = useCallback(async (cardBackId: string) => {
    if (!profile) return;
    const newEquipped = { ...profile.equipped, cardBack: cardBackId };
    setProfile({ ...profile, equipped: newEquipped });

    if (uid && uid !== 'guest') {
      try {
        await update(ref(db, `users/${uid}`), { equipped: newEquipped });
      } catch (error) {
        console.error('[useUserProfile] Error equipping skin in Firebase:', error);
      }
    }
  }, [uid, profile]);

  const updateOwnedSkins = useCallback(async (updates: Partial<OwnedSkins>) => {
    if (!profile) return;
    const newOwned = { ...profile.owned, ...updates } as OwnedSkins;
    const newEquipped = updates.cardBacks
      ? { ...profile.equipped, cardBack: updates.cardBacks[updates.cardBacks.length - 1] }
      : profile.equipped;
    setProfile({ ...profile, owned: newOwned, equipped: newEquipped });

    if (uid && uid !== 'guest') {
      try {
        await update(ref(db, `users/${uid}`), { owned: newOwned, equipped: newEquipped });
      } catch (error) {
        console.error('[useUserProfile] Error updating owned skins in Firebase:', error);
      }
    }
  }, [uid, profile]);

  return {
    profile,
    loading,
    updateDisplayName,
    updateProfileCustomization,
    updateOwnedSkins,
    equipSkin,
    recordMatchOutcome,
  };
}
